import React, { useEffect, useMemo, useReducer, useState, useCallback } from 'react';
import { request } from '../../../interfaces/networkManager';
// Import depuis le package spécifique à la v7
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { frFR } from '@mui/x-data-grid/locales';
import moment from 'moment';
import { Grid, Typography, TextField, Box, Button } from '@mui/material';
import { getCurrentUrl } from '../../../services/ApiServices';
import PlanetCreationObjectifModal from './PlanetCreationObjetifModal';
import DeleteIcon from '@mui/icons-material/Delete';
import { headers } from '../../../services/genericConstantes';
import { CompleteObjectif } from '../../../models/completeObjectif';
import { Version } from '../../../models/version';

interface Props {
	version: Version;
	setError: (error: string | null) => void;
	setSuccessSnackOpen: (status: boolean) => void;
}

interface ActionReducer {
	type: 'initObjective' | 'handleNameChange' | 'handleLongNameChange' | 'handleIndexesChange' | 'handleParametersChange' | 'handleClearAll';
	payload?: any;
}

interface AnimalIndex {
	idAnimalIndex: string;
	code?: string;
	name?: string;
	value?: number;
	animal_index?: {
		code: string;
	};
	lumpSumValue?: number;
	typePenalty?: string;
	avgPenalty?: number;
	stdDeviationPenalty?: number;
	avgISE?: number;
	stdDeviationISE?: number;
	weightISE?: number;
	upperMilestoneInvariance?: number;
	lowerMilestoneInvariance?: number;
	upperMilestone?: number;
	addingConstant?: number;
	multipleConstant?: number;
	weightingPenaltySPU?: number;
	thresholdOperator?: string;
	thresholdValue?: number;
}

interface Objectif {
	id: string;
	name: string;
	longName: string;
	STA_A1?: number;
	STA_A2?: number;
	minValueSTA?: number;
	maxValueSTA?: number;
	minPercentSTA?: number;
	maxPercentSTA?: number;
	handicap?: number;
	indexes: AnimalIndex[];
	dateCreated?: string;
	dateModified?: string;
}

type DispatchMemo = {
	target: {
		value: string;
	};
	value?: string;
};

const objectiveReducer = (objectif: Objectif | null, action: ActionReducer) => {
	switch (action.type) {
		case 'initObjective':
			return action.payload as Objectif;
		case 'handleNameChange':
			return objectif ? { ...objectif, name: action.payload } : null;
		case 'handleLongNameChange':
			return objectif ? { ...objectif, longName: action.payload } : null;
		case 'handleIndexesChange':
			return objectif ? { ...objectif, indexes: action.payload } : null;
		case 'handleParametersChange':
			return action.payload as Objectif;
		case 'handleClearAll':
			return null;
	}
	return objectif; // Default case to satisfy TypeScript
};

const PlanetTable: React.FC<Props> = ({ version, setError, setSuccessSnackOpen }) => {
	const [objectives, setObjectives] = useState<Objectif[]>([]);
	const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>({ ids: new Set(), type: 'include' });
	const [objectif, dispatch] = useReducer(objectiveReducer, null);
	const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);

	const objectifReducerMemo = useMemo(
		() => ({
			handleNameChange: (v: DispatchMemo) => dispatch({ type: 'handleNameChange', payload: v.target.value }),
			handleLongNameChange: (v: DispatchMemo) => dispatch({ type: 'handleLongNameChange', payload: v.target.value }),
			handleParametersChange: (v: CompleteObjectif) => dispatch({ type: 'handleParametersChange', payload: v }),
			handleIndexesChange: (v: any[]) => dispatch({ type: 'handleIndexesChange', payload: v }),
			handleClearAll: () => dispatch({ type: 'handleClearAll', payload: null }),
		}),
		[]
	);

	const getObjectives = useCallback(async () => {
		request<Objectif[]>(`/mating-plan-objectives/version/${version.id}`)
			.then((response: Objectif[]) => {
				setObjectives(response);
				objectifReducerMemo.handleClearAll();
			})
			.catch((error: string) => {
				setError('Erreur lors de la récupération du détail de l objectif');
			});
	}, [version.id, setObjectives, objectifReducerMemo, setError]);

	/**
	 * On récupère les objectifs de la version
	 */
	useEffect(() => {
		getObjectives();
	}, [version, setError, objectifReducerMemo, getObjectives]);

	/**
	 * On récupère un objectif complet après un clique sur ligne
	 */
	useEffect(() => {
		if (rowSelectionModel.ids.size > 0) {
			// Get the first ID from the Set
			const firstId = Array.from(rowSelectionModel.ids)[0];
			request<any>(`/mating-plan-objective/${firstId}`)
				.then((response: CompleteObjectif) => {
					objectifReducerMemo.handleParametersChange(response);
				})
				.catch((error: string) => {
					setError('Erreur lors de la récupération du détail de l objectif');
				});
		}
	}, [rowSelectionModel, setError, objectifReducerMemo]);

	/**
	 * Update row editable
	 */
	const processRowUpdateMemo = useCallback(
		(newRow: any) => {
			if (!objectif || !objectif.indexes) return newRow;
			
			const newIndexes = objectif.indexes.map((index: any) => {
				if (index.idAnimalIndex === newRow.idAnimalIndex) {
					return { ...newRow };
				}
				return index;
			});

			objectifReducerMemo.handleIndexesChange(newIndexes);

			return newRow;
		},
		[objectif, objectifReducerMemo]
	);

	const handleProcessRowUpdateErrorMemo = useCallback((error: string) => {
		console.error(error);
	}, []);

	/**
	 * Création d'un objectif
	 * @param name
	 * @param code
	 */
	const handleObjectifCreate = async (name: string, code: string) => {
		const objectifDto = {
			idMatingPlanObjectiveVersion: version.id,
			name: code,
			longName: name,
			numObjective: objectives.length ? Math.max(...objectives.map((o: any) => o.numObjective)) + 1 : 1,
			addingConstant: 0,
			multipleConstant: 0,
			handicap: 0,
			centerISE: 0,
			ecartTypeISE: 0,
			minValueSTA: 0,
			maxValueSTA: 0,
			minPercentSTA: 0,
			maxPercentSTA: 0,
		};

		const postOptions = {
			headers: headers(),
			method: 'POST',
			body: JSON.stringify(objectifDto),
		};

		fetch(`${getCurrentUrl()}/mating-plan-objective`, postOptions)
			.then(async (response: any) => {
				if (response.ok) {
					setSuccessSnackOpen(true);
					setIsCreationModalOpen(false);
					getObjectives();
				} else {
					setError("Erreur lors de la duplication de l'objectif");
				}
			})
			.catch((error: string) => {
				setError("Une erreur est survenue lors de la sauvegarde de l'objectif.");
			});
	};

	/**
	 * Update d'un objectif
	 */
	const handleUpdate = () => {
		if (checkObjectiveValidity()) setError('Certaines données ne sont pas valides.');
		else {
			if (!objectif) {
				setError('No objective selected to save');
				return;
			}
			
			const postOptions = {
				headers: headers(),
				method: 'PUT',
				body: JSON.stringify(objectif),
			};

			fetch(`${getCurrentUrl()}/mating-plan-objective/${objectif.id}`, postOptions)
				.then(async (response: any) => {
					if (response.ok) {
						setSuccessSnackOpen(true);
					} else {
						setError("Erreur lors de la duplication de l'objectif");
					}
				})
				.catch((error: string) => {
					setError("Une erreur est survenue lors de la sauvegarde de l'objectif.");
				});
		}
	};

	const checkObjectiveValidity = () => objectif && objectif.STA_A1 !== undefined && objectif.STA_A2 !== undefined && Number(objectif.STA_A1) + Number(objectif.STA_A2) !== 1;

	/**
	 * Delete an objectif
	 * @param value objectif to delete
	 */
	const handleObjectifDelete = useCallback((value: any) => {
		if (!value || !value.id) {
			setError('No objective selected to delete');
			return;
		}

		const deleteOptions = {
			headers: headers(),
			method: 'DELETE',
		};

		fetch(`${getCurrentUrl()}/mating-plan-objective/${value.id}`, deleteOptions)
			.then(async (response: any) => {
				if (response.ok) {
					setSuccessSnackOpen(true);
					getObjectives();
					if (objectif && objectif.id === value.id) {
						dispatch({ type: 'handleClearAll' });
					}
				} else {
					const errorText = await response.text();
					setError(errorText || 'An error occurred when deleting the objective');
				}
			})
			.catch((error) => {
				setError('An error occurred when deleting the objective');
			});
	}, [setError, setSuccessSnackOpen, getObjectives, objectif, dispatch]);

	const columns = useMemo<GridColDef[]>(
		() => [
			{
				field: 'id',
				headerName: 'ID',
				width: 100,
				hide: true,
			},
			{
				field: 'name',
				headerName: 'Code',
				flex: 1,
			},
			{
				field: 'longName',
				headerName: 'Libelle',
				flex: 2,
			},
			{
				field: 'dateModified',
				headerName: 'Date de modification',
				flex: 1,
				valueFormatter: (params: any) => params.value && moment(params.value).format('DD/MM/YYYY'),
			},
			{
				field: 'dateCreated',
				headerName: 'Date de création',
				flex: 1,
				valueFormatter: (params: any) => params.value && moment(params.value).format('DD/MM/YYYY'),
			},
			{
				field: 'action',
				headerName: 'Action',
				flex: 1,
				renderCell: (params) => (
					<DeleteIcon onClick={() => handleObjectifDelete(params.row)} color="error" />
				),
			},
		],
		[handleObjectifDelete]
	);

	const columnsForIndexes = useMemo<GridColDef[]>(
		() => [
			{
				headerName: 'Index',
				field: 'code',
				type: 'string',
				align: 'center',
				headerAlign: 'center',
				flex: 0.8,
				valueGetter: (value, row) => {
					if (row && row.animal_index) {
						return row.animal_index.code;
					}
					return '';
				},
			},
			{
				headerName: 'VALSTA',
				field: 'lumpSumValue',
				type: 'number',
				align: 'center',
				headerAlign: 'center',
				flex: 1,
				editable: true,
			},
			{
				headerName: 'Type Pénalité',
				field: 'typePenalty',
				type: 'singleSelect',
				align: 'center',
				headerAlign: 'center',
				flex: 0.8,
				valueOptions: [
					{ value: 'E', label: 'E' },
					{ value: 'I', label: 'I' },
					{ value: 'N', label: 'N' },
				],
				editable: true,
			},
			{
				headerName: 'Moy. Pénalité',
				field: 'avgPenalty',
				type: 'number',
				align: 'center',
				headerAlign: 'center',
				flex: 1,
				editable: true,
			},
			{
				headerName: 'Ect Pénalité',
				field: 'stdDeviationPenalty',
				type: 'number',
				align: 'center',
				headerAlign: 'center',
				flex: 1,
				editable: true,
			},
			{
				headerName: 'Moy. ISE',
				field: 'avgISE',
				type: 'number',
				align: 'center',
				headerAlign: 'center',
				flex: 1,
				editable: true,
			},
			{
				headerName: 'Ect ISE',
				field: 'stdDeviationISE',
				type: 'number',
				align: 'center',
				headerAlign: 'center',
				flex: 1,
				editable: true,
			},
			{
				headerName: 'Pond. ISE',
				field: 'weightISE',
				type: 'number',
				align: 'center',
				headerAlign: 'center',
				flex: 1,
				editable: true,
			},
			{
				headerName: 'B. Sup. Inv',
				field: 'upperMilestoneInvariance',
				type: 'number',
				align: 'center',
				headerAlign: 'center',
				flex: 1,
				editable: true,
			},
			{
				headerName: 'B. Inf. Inv',
				field: 'lowerMilestoneInvariance',
				type: 'number',
				align: 'center',
				headerAlign: 'center',
				flex: 1,
				editable: true,
			},
			{
				headerName: 'B. Sup. Obj',
				field: 'upperMilestone',
				type: 'number',
				align: 'center',
				headerAlign: 'center',
				flex: 1,
				editable: true,
			},
			{
				headerName: 'N',
				field: 'addingConstant',
				type: 'number',
				align: 'center',
				headerAlign: 'center',
				flex: 1,
				editable: true,
			},
			{
				headerName: 'D',
				field: 'multipleConstant',
				type: 'number',
				align: 'center',
				headerAlign: 'center',
				flex: 1,
				editable: true,
			},
			{
				headerName: 'Pond. Pénalité',
				field: 'weightingPenaltySPU',
				type: 'number',
				align: 'center',
				headerAlign: 'center',
				flex: 1,
				editable: true,
			},
			{
				headerName: 'F Seuil',
				field: 'thresholdOperator',
				type: 'singleSelect',
				align: 'center',
				headerAlign: 'center',
				flex: 1,
				editable: true,
			},
			{
				headerName: 'V Seuil',
				field: 'thresholdValue',
				type: 'number',
				align: 'center',
				headerAlign: 'center',
				flex: 1,
				editable: true,
			},
		],
		[]
	);

	return (
		<>
			<PlanetCreationObjectifModal isOpen={isCreationModalOpen} setIsOpen={setIsCreationModalOpen} onValidate={handleObjectifCreate} />
			<Grid container spacing={2}>
				<Box>
					<Typography variant="h6">Liste des objectifs</Typography>
				</Box>
				<Box sx={{ minHeight: 300 }}>
					<Box sx={{ height: 600 }}>
						<DataGrid
							getRowId={(row) => row.id}
							initialState={{
								sorting: {
									sortModel: [{ field: 'dateCreated', sort: 'desc' }],
								},
							}}
							localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
							rows={objectives || []}
							columns={columns}
							rowSelectionModel={rowSelectionModel}
							onRowSelectionModelChange={setRowSelectionModel}
							density="compact"
						/>
					</Box>
				</Box>
				{objectif && (
					<>
						<Box>
							<Typography variant="h6">Détail de l'objectif</Typography>
						</Box>
						<Box>
							<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 2 }}>
								<Box>
									<TextField label="STA_A1" fullWidth required value={objectif?.STA_A1?.toString() || ''} onChange={(e) => objectif && objectifReducerMemo.handleParametersChange(Object.assign({}, objectif, { STA_A1: Number(e.target.value) }) as unknown as CompleteObjectif)} />
								</Box>
								<Box>
									<TextField label="STA_A2" fullWidth required value={objectif?.STA_A2?.toString() || ''} onChange={(e) => objectif && objectifReducerMemo.handleParametersChange(Object.assign({}, objectif, { STA_A2: Number(e.target.value) }) as unknown as CompleteObjectif)} />
								</Box>
								<Box>
									<TextField label="Min Value STA" fullWidth required value={objectif?.minValueSTA?.toString() || ''} onChange={(e) => objectif && objectifReducerMemo.handleParametersChange(Object.assign({}, objectif, { minValueSTA: Number(e.target.value) }) as unknown as CompleteObjectif)} />
								</Box>
								<Box>
									<TextField label="Max Value STA" fullWidth required value={objectif?.maxValueSTA?.toString() || ''} onChange={(e) => objectif && objectifReducerMemo.handleParametersChange(Object.assign({}, objectif, { maxValueSTA: Number(e.target.value) }) as unknown as CompleteObjectif)} />
								</Box>
								<Box>
									<TextField label="MIN % STA" fullWidth required value={objectif?.minPercentSTA?.toString() || ''} onChange={(e) => objectif && objectifReducerMemo.handleParametersChange(Object.assign({}, objectif, { minPercentSTA: Number(e.target.value) }) as unknown as CompleteObjectif)} />
								</Box>
								<Box>
									<TextField label="MAX % STA" fullWidth required value={objectif?.maxPercentSTA?.toString() || ''} onChange={(e) => objectif && objectifReducerMemo.handleParametersChange(Object.assign({}, objectif, { maxPercentSTA: Number(e.target.value) }) as unknown as CompleteObjectif)} />
								</Box>
								<Box>
									<TextField label="Handicap" fullWidth required value={objectif?.handicap?.toString() || ''} onChange={(e) => objectif && objectifReducerMemo.handleParametersChange(Object.assign({}, objectif, { handicap: Number(e.target.value) }) as unknown as CompleteObjectif)} />
								</Box>
								<Box sx={{ gridColumn: '1 / -1', minHeight: 300 }}>
									<Box sx={{ height: 600 }}>
										<DataGrid
											getRowId={(row) => row.idAnimalIndex}
											onProcessRowUpdateError={handleProcessRowUpdateErrorMemo}
											processRowUpdate={processRowUpdateMemo}
											rows={objectif?.indexes || []}
											columns={columnsForIndexes}
											density="compact"
											editMode="row"
										/>
									</Box>
								</Box>
							</Box>

							<Box display="flex" justifyContent="flex-end" mt={1}>
								<Button variant="outlined" onClick={handleUpdate} aria-label="Enregistrer l'objectif">
									Enregistrer l'objectif
								</Button>
							</Box>
						</Box>
					</>
				)}
			</Grid>
		</>
	);
};

export default PlanetTable;
